/**
 * dealHealthCard.ts — the pure half of the deal page's health card (deal
 * detail slice 6, §39.9 order 6; spec §3.4 A), since rule set 2 (§40.12,
 * Revenue Recovery R1) a FORMATTER: the six checks, the score, the status
 * and the stage bar's threshold are decided on the SERVER
 * (internal/modules/sales/health, `deal.assessment`) — the same evaluation
 * the board badge renders, so the two cannot disagree. This module only words
 * what the server decided: a check's `reason` and its `days` / `count`, plus
 * the names and money the page already holds.
 *
 * Every day count is a Europe/London CALENDAR day on the SERVER's today
 * (assessment.today). Data honesty (§39.4 item 1, §35): a figure the read
 * could not produce is LEFT OUT of a sentence, never printed as 0.
 */
import type { DealDetail, DealStage } from '../../types'
import type { DealAssessment, DealHealthCheck, SalesDealPipelinePosition } from '../../../../api/types'
import { compactMoney } from '../../../../lib/money'
import { formatDay } from './dealRail'
import { STAGE_STEPS } from './stageBar'
import { londonDay } from './workingDays'

export type CheckId = 'value' | 'closeDate' | 'contact' | 'recentActivity' | 'nextStep' | 'stageProgress'
export interface HealthCheck { id: CheckId; label: string; pass: boolean; note: string }
export type HealthStatus = 'on-track' | 'needs-attention' | 'at-risk'

/** Revenue Recovery R3: the flag RULE a check belongs to (the ledger's four
 *  families); value, next step and stage progress are not flag rules and carry
 *  no act. Stage age is a rule with no tile (the stage bar renders it). */
export const CHECK_RULE: Readonly<Partial<Record<CheckId, string>>> = {
  closeDate: 'close_date',
  contact: 'contact',
  recentActivity: 'recent_activity',
}
export const STAGE_AGE_RULE = 'stage_age'
/** R6 (rule set 3): the committee family — a signal, not a scored check, so
 *  like stage age it has its own row under the grid, not a tile. */
export const COMMITTEE_RULE = 'committee'
export const NO_DECISION_MAKER_NOTE = 'No decision maker linked on this deal. Set a role on the People card.'
/** The same fact for a session that cannot set a role (§44: no instruction it cannot follow). */
export const NO_DECISION_MAKER_FACT = 'No decision maker linked on this deal.'
/** §40.13 ruling 4: a failing flag-rule check with no open episode yet. */
export const AFTER_TONIGHT_COPY = "Can be confirmed or dismissed after tonight's check"

/** The rule set's recent-activity window (§40.12 ruling 2), restated for the
 *  close-date Keep rule, which reads the server's last completed call, email
 *  or meeting against it. The Recent activity CHECK is decided on the server. */
export const RECENT_ACTIVITY_DAYS = 14

const DAY_MS = 86_400_000
const dayMs = (iso: string): number => Date.parse(`${iso}T00:00:00Z`)

/** Whole London calendar days from `fromIso`'s day to `toIso` (both
 *  YYYY-MM-DD); negative when `fromIso` is later. */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((dayMs(toIso) - dayMs(fromIso)) / DAY_MS)
}

/** The London day an instant falls on, or null for a value that is not one. */
export function londonDayOf(iso: string | null | undefined): string | null {
  if (!iso) return null
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? null : londonDay(t)
}

/** The close DATE the wire stores (a date at UTC midnight): its calendar day
 *  as written, never shifted by a zone. */
export function closeDay(expectedClose: string | null | undefined): string | null {
  if (!expectedClose) return null
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(expectedClose)
  return m ? m[1] : null
}

const plural = (n: number, one: string, many: string) => (n === 1 ? `${n} ${one}` : `${n} ${many}`)

/** Steps after the current one up to Won: New Lead 4 … Negotiation 1; null
 *  for a closed or unknown stage. */
export function stagesLeft(stage: DealStage): number | null {
  const i = STAGE_STEPS.indexOf(stage)
  if (i < 0 || i >= STAGE_STEPS.length - 1) return null
  return STAGE_STEPS.length - 1 - i
}

/** The London day of the server's last completed call, email or meeting
 *  (the Recent activity check's evidence); null when there is none. */
export function lastLoggedDay(assessment: DealAssessment | null | undefined): string | null {
  const c = assessment?.checks.find(x => x.id === 'recentActivity')
  if (!c || (c.reason !== 'logged' && c.reason !== 'quiet')) return null
  return londonDayOf(c.evidence[0]?.at)
}

export interface CheckInput {
  deal: Pick<DealDetail, 'amountPence' | 'currency' | 'expectedClose' | 'company' | 'stage'>
  /** The server's evaluation — every pass / fail below is its decision. */
  assessment: DealAssessment
  /** The linked contact's name when it has loaded (the pass note names them). */
  contactName?: string | null
  /** The deal's links (GET /contacts), for the primary's NAME only — how many
   *  are linked is the server's count. Absent / null while the read is in
   *  flight or failed: the note falls back to the primary mirror's name. */
  linked?: { count: number; primaryName: string | null } | null
}

const num = (c: DealHealthCheck | undefined, k: 'days' | 'count'): number | null => (c && typeof c[k] === 'number' ? c[k] as number : null)

/** The six checks of spec §3.4 A, in the grid's order, worded from the
 *  server's decisions. */
export function healthChecks({ deal, assessment, contactName, linked }: CheckInput): HealthCheck[] {
  const by = (id: CheckId) => assessment.checks.find(c => c.id === id)
  const passOf = (id: CheckId) => by(id)?.pass === true
  const close = closeDay(deal.expectedClose)

  const value: HealthCheck = passOf('value')
    ? { id: 'value', label: 'Value', pass: true, note: compactMoney(deal.amountPence, deal.currency) }
    : { id: 'value', label: 'Value', pass: false, note: compactMoney(0, deal.currency) }

  const cd = by('closeDate')
  const toClose = num(cd, 'days')
  let closeDate: HealthCheck
  if (cd?.reason === 'close_ahead' && close && toClose != null) closeDate = { id: 'closeDate', label: 'Close date', pass: passOf('closeDate'), note: `${formatDay(close, 'utc')}, in ${plural(toClose, 'day', 'days')}` }
  else if (cd?.reason === 'close_today') closeDate = { id: 'closeDate', label: 'Close date', pass: false, note: 'Close date is today' }
  else if (cd?.reason === 'close_passed' && close) closeDate = { id: 'closeDate', label: 'Close date', pass: false, note: `Close date passed ${formatDay(close, 'utc')}` }
  else closeDate = { id: 'closeDate', label: 'Close date', pass: false, note: 'No close date' }

  const ct = by('contact')
  const count = num(ct, 'count') ?? 0
  const name = linked ? linked.primaryName : (contactName ?? null)
  let contact: HealthCheck
  if (ct?.reason === 'no_contact' || count < 1) {
    contact = { id: 'contact', label: 'Contact', pass: false, note: deal.company ? `No one from ${deal.company} is linked` : 'No contact is linked' }
  } else if (ct?.reason === 'single_threaded') {
    contact = { id: 'contact', label: 'Contact', pass: false, note: name ? `Only ${name} is linked on a £1M+ deal` : 'Only one contact is linked on a £1M+ deal' }
  } else {
    contact = {
      id: 'contact', label: 'Contact', pass: passOf('contact'),
      note: count === 1
        ? (name ? `${name} is linked` : 'A contact is linked')
        : (name ? `${name} and ${plural(count - 1, 'more', 'more')} linked` : `${count} contacts are linked`),
    }
  }

  const ra = by('recentActivity')
  const quiet = num(ra, 'days')
  let recentActivity: HealthCheck
  if (ra?.reason === 'logged') {
    recentActivity = { id: 'recentActivity', label: 'Recent activity', pass: passOf('recentActivity'), note: !quiet ? 'Last logged today' : `Last logged ${plural(quiet, 'day', 'days')} ago` }
  } else {
    recentActivity = { id: 'recentActivity', label: 'Recent activity', pass: false, note: quiet != null && quiet > 0 ? `Nothing logged in ${plural(quiet, 'day', 'days')}` : 'Nothing logged yet' }
  }

  const ns = by('nextStep')
  const planned = num(ns, 'count') ?? 0
  const nextStep: HealthCheck = passOf('nextStep')
    ? { id: 'nextStep', label: 'Next step', pass: true, note: planned === 1 ? '1 open task with a due date' : `${planned} open tasks with a due date` }
    : { id: 'nextStep', label: 'Next step', pass: false, note: 'No task planned' }

  const sp = by('stageProgress')
  const left = num(sp, 'count')
  const spDays = num(sp, 'days')
  let stageProgress: HealthCheck
  if (sp?.reason === 'close_passed') {
    stageProgress = { id: 'stageProgress', label: 'Stage progress', pass: false, note: 'The close date has passed' }
  } else if ((sp?.reason === 'on_pace' || sp?.reason === 'behind') && left != null && spDays != null) {
    const span = `${plural(left, 'stage', 'stages')} in ${plural(spDays, 'day', 'days')}`
    const pastFirst = STAGE_STEPS.indexOf(deal.stage) >= 1
    stageProgress = sp.reason === 'on_pace'
      ? { id: 'stageProgress', label: 'Stage progress', pass: passOf('stageProgress'), note: `On pace: ${span}` }
      : { id: 'stageProgress', label: 'Stage progress', pass: false, note: pastFirst ? `${deal.stage}, ${span}` : `Still ${stageWord(deal.stage)}, ${span}` }
  } else {
    stageProgress = { id: 'stageProgress', label: 'Stage progress', pass: false, note: 'No close date to measure against' }
  }

  return [value, closeDate, contact, recentActivity, nextStep, stageProgress]
}

/** "New lead" — the spec writes the first stage in sentence case in its note. */
function stageWord(stage: DealStage): string {
  return stage === 'New Lead' ? 'New lead' : stage
}

/** Spec §3.4 A: 6 On track, 4–5 Needs attention, 0–3 At risk. */
export function statusFor(score: number): HealthStatus {
  if (score >= 6) return 'on-track'
  if (score >= 4) return 'needs-attention'
  return 'at-risk'
}
export const STATUS_LABEL: Record<HealthStatus, string> = { 'on-track': 'On track', 'needs-attention': 'Needs attention', 'at-risk': 'At risk' }

/** "At risk: 4 things to fix" / "Getting there: 1 thing to fix" / "On track". */
export function headlineFor(score: number, total = 6): string {
  const fails = total - score
  const things = fails === 1 ? '1 thing to fix' : `${fails} things to fix`
  const st = statusFor(score)
  if (st === 'on-track') return 'On track'
  return st === 'needs-attention' ? `Getting there: ${things}` : `At risk: ${things}`
}

const ORDINAL = ['', '', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth']
function ordinalSuffix(n: number): string {
  const t = n % 100
  if (t >= 11 && t <= 13) return `${n}th`
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`
}
/** "Your biggest open deal" / "Your second-biggest open deal" / "Your 12th-biggest open deal". */
export function rankPhrase(rank: number): string {
  if (rank === 1) return 'Your biggest open deal'
  return `Your ${ORDINAL[rank] ?? ordinalSuffix(rank)}-biggest open deal`
}

/** Whole percent of basis points; "under 1%" for a share that rounds to 0 %.
 *  `dealValuePence` tells a TRUE zero (a deal with no value: "0%") from a
 *  share the server rounded to 0 bp (a £1 deal against £11.78M is 0.00085 bp):
 *  a deal that has value never reads "0%". */
export function sharePercent(bp: number, dealValuePence: number | null = null): string {
  if (bp > 0 && bp < 50) return 'under 1%'
  if (bp === 0 && dealValuePence != null && dealValuePence > 0) return 'under 1%'
  return `${Math.round(bp / 100)}%`
}

export interface SummaryInput {
  position: SalesDealPipelinePosition | null
  /** The server's evaluation: its Recent activity check carries the quiet
   *  days — from the last completed call, email or meeting, else creation. */
  assessment: DealAssessment | null | undefined
}

/**
 * The card's one sentence (spec §3.4 A summary rule): the deal's rank among
 * open deals, its share of the open pipeline, and the days since the last
 * call, email or meeting — or since creation when there is none. Each part
 * the read could not produce (a null rank, share or total; a failed read) is
 * left out of the sentence, never shown as 0. Open deals the fence could not
 * value are said, when there are any (§39.4 item 2).
 */
export function summarySentence({ position, assessment }: SummaryInput): string {
  const lead: string[] = []
  if (position && position.open) {
    if (position.rank != null) lead.push(rankPhrase(position.rank))
    if (position.shareBasisPoints != null && position.openPipelinePence != null) {
      lead.push(`${sharePercent(position.shareBasisPoints, position.dealValuePence)} of the open pipeline (${compactMoney(position.openPipelinePence)})`)
    }
  }
  const ra = assessment?.checks.find(c => c.id === 'recentActivity')
  const n = num(ra, 'days')
  let quiet: string | null = null
  if (ra && n != null && (ra.reason === 'logged' || ra.reason === 'quiet')) {
    quiet = n <= 0 ? 'the last call, email or meeting was logged today' : `the last call, email or meeting was logged ${plural(n, 'day', 'days')} ago`
  } else if (ra && n != null) {
    quiet = n <= 0 ? 'nothing has been logged since it was created today' : `nothing has been logged since it was created ${plural(n, 'day', 'days')} ago`
  }
  let s: string
  if (lead.length && quiet) s = `${lead.join(', ')}, and ${quiet}.`
  else if (lead.length) s = `${lead.join(', ')}.`
  else if (quiet) s = `${quiet[0].toUpperCase()}${quiet.slice(1)}.`
  else s = ''
  const unvalued = position && position.open ? position.uncapturedOpenDeals : 0
  if (unvalued > 0) {
    const tail = unvalued === 1
      ? '1 open deal in another currency has no captured value and is not counted.'
      : `${unvalued} open deals in other currencies have no captured value and are not counted.`
    s = s ? `${s} ${tail}` : tail
  }
  return s
}

/** The spec's footer, verbatim. */
export const HEALTH_FOOTER = 'Checked against the rules for an open deal: value, close date, a linked contact, activity in the last 14 days, a planned next step, and enough stage progress for the close date.'
